import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import DonutSmallOutlinedIcon from "@mui/icons-material/DonutSmallOutlined";

interface InventoryDistributionProps {
  data: Array<{
    categoryId: string;
    categoryName: string;
    availableStock: number;
    inventoryValue: number;
  }>;
}

const InventoryDistribution = ({ data }: InventoryDistributionProps) => {
  const rows = data.slice(0, 6);
  const max = Math.max(...rows.map((row) => row.availableStock), 1);

  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        borderRadius: 2,
        border: "1px solid",
        borderColor: theme.palette.mode === "dark" ? "#2a3953" : "divider",
        bgcolor: theme.palette.mode === "dark" ? "#111b2d" : "background.paper",
        minWidth: 0,
      })}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 1.75 }, "&:last-child": { pb: { xs: 1.5, md: 1.75 } } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          mb={1.25}
          minWidth={0}
          flexWrap={{ xs: "wrap", sm: "nowrap" }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ width: 38, height: 38, display: "grid", placeItems: "center", borderRadius: 2, bgcolor: "secondary.light", color: "secondary.dark" }}>
              <DonutSmallOutlinedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={800} noWrap>Inventory Distribution</Typography>
              <Typography variant="caption" color="text.secondary" noWrap>Stock distribution by category</Typography>
            </Box>
          </Stack>
          <Chip
            label={`Total: ${rows.reduce((total, row) => total + row.availableStock, 0)} units`}
            variant="outlined"
            size="small"
            sx={{ flex: "0 0 auto", ml: { xs: 5.75, sm: 0 } }}
          />
        </Stack>

        <Stack spacing={1.1}>
          {rows.length === 0 && (
            <Typography color="text.secondary">
              No inventory data available yet.
            </Typography>
          )}

          {rows.map((row) => (
            <Box key={row.categoryId}>
              <Box display="flex" justifyContent="space-between" mb={0.45} gap={1}>
                <Typography variant="body2" fontWeight={700} noWrap>
                  {row.categoryName}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  {row.availableStock}
                </Typography>
              </Box>

              <Box
                sx={{
                  height: 8,
                  borderRadius: 999,
                  bgcolor: (theme) => theme.palette.mode === "dark" ? "#26334a" : "action.hover",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${Math.max((row.availableStock / max) * 100, 4)}%`,
                    background: "linear-gradient(90deg, #34D399, #0EA5E9)",
                  }}
                />
              </Box>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default InventoryDistribution;
